import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Award, CheckCircle2, Copy, Download, Mail, Printer, RotateCcw, Share2, XCircle } from 'lucide-react';
import { MPD_DOSSIERS, MPD_META } from '../../data/premierDepartData';
import { renderCertificatePng } from './certificatePng';

const QUESTIONS_PER_SESSION = 20;
const SEUIL = 80;

function buildBank() {
  const bank = [];
  MPD_DOSSIERS.forEach((d) => {
    d.chapters.forEach((c) => {
      if (c.quiz) c.quiz.questions.forEach((q) => bank.push({ ...q, dossierId: d.id, dossierNum: d.num, dossierTitle: d.title, chapterSlug: c.slug }));
    });
  });
  return bank;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function drawSession(bank) {
  const byDossier = {};
  bank.forEach((q) => { (byDossier[q.dossierId] = byDossier[q.dossierId] || []).push(q); });
  const groups = Object.values(byDossier).map((qs) => shuffle(qs));
  const picked = [];
  groups.forEach((qs) => { if (qs.length) picked.push(qs.shift()); });
  let rest = shuffle(groups.flat());
  while (picked.length < QUESTIONS_PER_SESSION && rest.length) picked.push(rest.shift());
  return shuffle(picked).slice(0, QUESTIONS_PER_SESSION);
}

export default function PremierDepartEvaluation() {
  const bank = useMemo(buildBank, []);
  const [session, setSession] = useState(() => drawSession(bank));
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);
  const [certName, setCertName] = useState('');
  const [showCert, setShowCert] = useState(false);

  const q = session[idx];
  const validated = selected !== null && answers.length > idx;

  const validate = () => {
    if (selected === null || validated) return;
    setAnswers([...answers, { correct: selected === q.answer, dossierId: q.dossierId, dossierNum: q.dossierNum, dossierTitle: q.dossierTitle }]);
  };

  const next = () => {
    if (idx + 1 >= session.length) { setFinished(true); return; }
    setIdx(idx + 1);
    setSelected(null);
    window.scrollTo(0, 0);
  };

  const restart = () => {
    setSession(drawSession(bank));
    setIdx(0); setSelected(null); setAnswers([]); setFinished(false);
    setShowCert(false);
  };

  const printCert = () => {
    document.body.classList.add('print-cert');
    window.print();
    setTimeout(() => document.body.classList.remove('print-cert'), 600);
  };

  const [shareMsg, setShareMsg] = useState('');
  const flash = (m) => { setShareMsg(m); setTimeout(() => setShareMsg(''), 3000); };
  const certData = () => ({ name: certName.trim(), score, total: answers.length, pct, cobrand: MPD_META.cobrand });
  const shareText = () => `🎓 ${certName.trim()} a obtenu le certificat « Mon premier départ » avec ${pct} % de réussite sur CodeVoiture.fr ! Découvrez le livret gratuit : ${window.location.origin}/mon-premier-depart`;

  const downloadCert = async () => {
    const blob = await renderCertificatePng(certData());
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `certificat-mon-premier-depart-${certName.trim().toLowerCase().replace(/\s+/g, '-')}.png`;
    a.click();
    URL.revokeObjectURL(url);
    flash('Certificat téléchargé en image PNG ✔');
  };

  const nativeShare = async () => {
    try {
      const blob = await renderCertificatePng(certData());
      const file = new File([blob], 'certificat-mon-premier-depart.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Certificat Mon premier départ', text: shareText() });
      } else if (navigator.share) {
        await navigator.share({ title: 'Certificat Mon premier départ', text: shareText() });
      } else {
        await downloadCert();
        flash("Partage direct non disponible sur cet appareil — l'image a été téléchargée.");
      }
    } catch (e) { if (e?.name !== 'AbortError') flash('Partage annulé ou indisponible.'); }
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText())}`, '_blank', 'noopener');
  const shareEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent('Mon certificat « Mon premier départ »')}&body=${encodeURIComponent(shareText() + '\n\nPensez à joindre l\'image du certificat téléchargée depuis le site.')}`; };
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareText()); flash('Message copié — collez-le où vous voulez ✔'); }
    catch { flash('Copie impossible sur cet appareil.'); }
  };

  const score = answers.filter((a) => a.correct).length;
  const pct = answers.length ? Math.round((score / answers.length) * 100) : 0;

  const byTheme = useMemo(() => {
    const m = {};
    answers.forEach((a) => {
      const t = m[a.dossierId] || (m[a.dossierId] = { num: a.dossierNum, title: a.dossierTitle, ok: 0, total: 0 });
      t.total += 1; if (a.correct) t.ok += 1;
    });
    return Object.entries(m);
  }, [answers]);

  return (
    <div className="min-h-screen bg-[#faf7f0] text-[#241f18]" data-testid="mpd-evaluation">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/mon-premier-depart" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-orange-700 hover:text-orange-500 mb-6" data-testid="mpd-eval-back">
          <ArrowLeft size={14} /> Retour au livret
        </Link>
        <p className="text-[11px] font-black uppercase tracking-[0.25em] text-orange-600 mb-2">Mon premier départ</p>
        <h1 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: 'Georgia, serif' }}>Évaluation finale</h1>
        <p className="text-[13px] text-[#8a8070] mb-8">
          Cette évaluation vérifie les connaissances abordées dans le livret. Elle ne remplace ni une formation pratique, ni un examen officiel, ni les conditions légales nécessaires à la conduite.
        </p>

        {!finished && q && (
          <div className="rounded-2xl bg-white border border-[#e8e0d0] p-6 shadow-sm" data-testid="mpd-eval-question-card">
            <div className="flex items-center justify-between gap-2 mb-4">
              <span className="text-[12px] font-black text-orange-600" data-testid="mpd-eval-counter">Question {idx + 1} / {session.length}</span>
              <span className="text-[11px] font-bold text-[#8a8070]">{q.dossierNum} — {q.dossierTitle}</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#f0e9db] mb-5 overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${((idx + (validated ? 1 : 0)) / session.length) * 100}%` }} />
            </div>
            <p className="font-black text-[16px] mb-4" data-testid="mpd-eval-question">{q.q}</p>
            <div className="space-y-2 mb-5">
              {q.options.map((opt, i) => {
                let cls = 'border-[#eee5d4] hover:border-orange-300';
                if (validated) {
                  if (i === q.answer) cls = 'border-emerald-400 bg-emerald-50';
                  else if (i === selected) cls = 'border-red-300 bg-red-50';
                  else cls = 'border-[#eee5d4] opacity-60';
                } else if (i === selected) cls = 'border-orange-400 bg-orange-50';
                return (
                  <button key={i} type="button" disabled={validated} onClick={() => setSelected(i)} data-testid={`mpd-eval-option-${i}`}
                    className={`w-full flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-colors ${cls}`}>
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#f0e9db] text-[12px] font-black flex items-center justify-center mt-0.5">{String.fromCharCode(65 + i)}</span>
                    <span className="text-[14px] font-semibold">{opt}</span>
                  </button>
                );
              })}
            </div>
            {validated && (
              <div className={`rounded-xl border-l-4 p-4 mb-5 ${answers[idx].correct ? 'border-emerald-500 bg-emerald-50' : 'border-red-400 bg-red-50'}`} data-testid="mpd-eval-explain">
                <p className="flex items-center gap-1.5 text-[12px] font-black uppercase tracking-wide mb-1">
                  {answers[idx].correct ? <><CheckCircle2 size={14} className="text-emerald-600" /> Bonne réponse</> : <><XCircle size={14} className="text-red-500" /> Réponse incorrecte</>}
                </p>
                <p className="text-[13.5px] leading-relaxed">{q.explain}</p>
              </div>
            )}
            {!validated ? (
              <button type="button" onClick={validate} disabled={selected === null} data-testid="mpd-eval-validate-btn"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white font-bold text-sm transition-colors">
                Valider ma réponse
              </button>
            ) : (
              <button type="button" onClick={next} data-testid="mpd-eval-next-btn"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#241f18] hover:bg-black text-white font-bold text-sm transition-colors">
                {idx + 1 >= session.length ? 'Voir mon bilan' : 'Question suivante'} <ArrowRight size={16} />
              </button>
            )}
          </div>
        )}

        {finished && (
          <div className="space-y-6" data-testid="mpd-eval-results">
            <div className={`rounded-2xl border-2 p-6 text-center ${pct >= SEUIL ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
              <Award size={36} className={`mx-auto mb-2 ${pct >= SEUIL ? 'text-emerald-600' : 'text-orange-500'}`} />
              <p className="text-4xl font-black mb-1" data-testid="mpd-eval-score">{score} / {answers.length}</p>
              <p className="text-[15px] font-bold mb-2">{pct} % de réussite</p>
              <p className="text-[13px] text-[#5a5245]">
                {pct >= SEUIL
                  ? 'Bravo ! Vous avez atteint le repère pédagogique de 80 %. Continuez à vous entraîner avec votre enseignant.'
                  : 'Le repère pédagogique est fixé à 80 %. Revoyez les dossiers ci-dessous puis recommencez l\'évaluation.'}
              </p>
              <p className="mt-3 text-[11px] text-[#8a8070]">Ce repère est pédagogique : il ne constitue pas un seuil officiel de réussite à un examen réglementaire.</p>
            </div>

            {pct >= SEUIL && (
              <div className="rounded-2xl bg-white border border-[#e8e0d0] p-5" data-testid="mpd-cert-section">
                <h2 className="font-black text-[15px] mb-2">🎓 Mon certificat « Mon premier départ »</h2>
                {!showCert ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input type="text" value={certName} onChange={(e) => setCertName(e.target.value)} placeholder="Ton prénom et ton nom"
                      maxLength={60} data-testid="mpd-cert-name-input"
                      className="flex-1 h-11 px-4 rounded-xl border border-[#e0d6c2] bg-[#faf7f0] text-[14px] font-semibold focus:outline-none focus:border-orange-400" />
                    <button type="button" disabled={!certName.trim()} onClick={() => setShowCert(true)} data-testid="mpd-cert-show-btn"
                      className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white font-bold text-sm transition-colors">
                      <Award size={15} /> Générer mon certificat
                    </button>
                  </div>
                ) : (
                  <>
                    <div id="mpd-certificat" data-testid="mpd-certificat"
                      className="rounded-2xl border-4 border-double border-orange-400 bg-[#fffdf8] p-8 text-center my-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-600 mb-3">{MPD_META.cobrand}</p>
                      <p className="text-[12px] font-bold text-[#8a8070] mb-1">Certificat de fin de parcours</p>
                      <h3 className="text-3xl font-black mb-4" style={{ fontFamily: 'Georgia, serif' }}>Mon premier départ</h3>
                      <p className="text-[13.5px] text-[#5a5245] mb-2">Ce certificat est décerné à</p>
                      <p className="text-2xl font-black text-[#241f18] mb-4" data-testid="mpd-cert-name">{certName.trim()}</p>
                      <p className="text-[13.5px] text-[#5a5245] mb-1">
                        pour avoir terminé l'évaluation finale du livret pédagogique avec un score de
                        <span className="font-black text-orange-700"> {score}/{answers.length} ({pct} %)</span>
                      </p>
                      <p className="text-[12px] text-[#8a8070] mb-5">le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="h-px w-16 bg-orange-300" />
                        <Award size={28} className="text-orange-500" />
                        <span className="h-px w-16 bg-orange-300" />
                      </div>
                      <p className="text-[10.5px] text-[#b5a98f] max-w-md mx-auto">
                        Attestation pédagogique délivrée par le livret « Mon premier départ ». Elle ne constitue ni un titre de conduite, ni un examen officiel, et ne remplace pas la formation pratique encadrée.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3 no-print">
                      <button type="button" onClick={printCert} data-testid="mpd-cert-print-btn"
                        className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#241f18] hover:bg-black text-white font-bold text-sm transition-colors">
                        <Printer size={15} /> Imprimer (PDF)
                      </button>
                      <button type="button" onClick={() => setShowCert(false)} className="text-[13px] font-bold text-[#8a8070] hover:text-orange-600">Modifier le nom</button>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#eee5d4] no-print" data-testid="mpd-cert-share-section">
                      <p className="text-[12px] font-black uppercase tracking-wide text-orange-700 mb-2.5">Partager mon certificat avec mon auto-école ou ma famille</p>
                      <div className="flex flex-wrap gap-2.5">
                        <button type="button" onClick={nativeShare} data-testid="mpd-cert-share-btn"
                          className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-orange-600 hover:bg-orange-500 text-white font-bold text-[13px] transition-colors">
                          <Share2 size={14} /> Partager
                        </button>
                        <button type="button" onClick={shareWhatsApp} data-testid="mpd-cert-whatsapp-btn"
                          className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#25D366] hover:bg-[#1fb857] text-white font-bold text-[13px] transition-colors">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          WhatsApp
                        </button>
                        <button type="button" onClick={shareEmail} data-testid="mpd-cert-email-btn"
                          className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#241f18] hover:bg-black text-white font-bold text-[13px] transition-colors">
                          <Mail size={14} /> E-mail
                        </button>
                        <button type="button" onClick={downloadCert} data-testid="mpd-cert-download-btn"
                          className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[#e0d6c2] bg-white hover:border-orange-300 text-[#3d362c] font-bold text-[13px] transition-colors">
                          <Download size={14} /> Image PNG
                        </button>
                        <button type="button" onClick={copyShare} data-testid="mpd-cert-copy-btn"
                          className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[#e0d6c2] bg-white hover:border-orange-300 text-[#3d362c] font-bold text-[13px] transition-colors">
                          <Copy size={14} /> Copier le message
                        </button>
                      </div>
                      {shareMsg && <p className="mt-2.5 text-[12.5px] font-bold text-emerald-700" data-testid="mpd-cert-share-msg">{shareMsg}</p>}
                    </div>
                    <style>{`@media print { body.print-cert > * { display: none !important; } body.print-cert #root { display: block !important; } body.print-cert #root * { visibility: hidden; } body.print-cert #mpd-certificat, body.print-cert #mpd-certificat * { visibility: visible; } body.print-cert #mpd-certificat { position: fixed; top: 10%; left: 5%; right: 5%; } }`}</style>
                  </>
                )}
              </div>
            )}
            <div className="rounded-2xl bg-white border border-[#e8e0d0] p-5">
              <h2 className="font-black text-[15px] mb-3">Mon bilan par thème</h2>
              <div className="space-y-2">
                {byTheme.map(([id, t]) => (
                  <div key={id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-[#eee5d4]" data-testid={`mpd-eval-theme-${id}`}>
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-bold truncate">{t.num} — {t.title}</p>
                      <p className={`text-[11.5px] font-black ${t.ok === t.total ? 'text-emerald-600' : 'text-orange-600'}`}>{t.ok}/{t.total} bonne{t.ok > 1 ? 's' : ''} réponse{t.ok > 1 ? 's' : ''}</p>
                    </div>
                    {t.ok < t.total && (() => {
                      const dossier = MPD_DOSSIERS.find((d) => d.id === id);
                      const slug = dossier?.chapters[0]?.slug;
                      return (
                        <Link to={slug ? `/mon-premier-depart/${id}/${slug}` : '/mon-premier-depart'} className="shrink-0 text-[12px] font-bold text-orange-700 hover:text-orange-500" data-testid={`mpd-eval-review-${id}`}>
                          Revoir ce dossier
                        </Link>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
            <button type="button" onClick={restart} data-testid="mpd-eval-restart-btn"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-orange-600 hover:bg-orange-500 text-white font-bold text-sm transition-colors">
              <RotateCcw size={15} /> Recommencer avec de nouvelles questions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
